import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import type { ReactNode } from "react";
import logo from "../../assets/skand-logo-long.png";
import { serviceGroups, services, site, solutions } from "../../constants/site";
import { buildWhatsAppUrl } from "../../utils/whatsapp";
import { LinkButton } from "../ui/Button";
import { Container } from "../common/Container";

export function Footer() {
  const primaryServices = serviceGroups
    .map((group) => services.find((service) => service.group === group))
    .filter(Boolean);

  return (
    <footer className="bg-ink-950 text-white">
      <Container className="py-16">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr_0.8fr_0.9fr]">
          <div>
            <img
              src={logo}
              alt="Skand Tech Hub"
              className="h-16 w-auto max-w-[240px] object-contain"
            />
            <p className="mt-5 max-w-md leading-7 text-white/60">
              A modern technology company delivering enterprise software,
              scalable web platforms, mobile applications, and digital
              transformation systems.
            </p>
            <div className="mt-6">
              <LinkButton to="/contact">Contact Us</LinkButton>
            </div>
          </div>
          <FooterColumn title="Services">
            {primaryServices.map((service) =>
              service ? (
                <Link key={service.slug} to={`/${service.slug}`}>
                  {service.title}
                </Link>
              ) : null,
            )}
          </FooterColumn>
          <FooterColumn title="Solutions">
            {solutions.slice(0, 6).map((solution) => (
              <Link key={solution.slug} to={`/${solution.slug}`}>
                {solution.title}
              </Link>
            ))}
          </FooterColumn>
          <div>
            <h2 className="font-semibold text-white">Contact</h2>
            <div className="mt-4 grid gap-3 text-sm text-white/60">
              <a
                href={`tel:${site.phone.replace(/\s/g, "")}`}
                className="flex gap-3 hover:text-gold-300"
              >
                <Phone className="h-4 w-4" /> {site.phone}
              </a>
              <a
                href={`mailto:${site.email}`}
                className="flex gap-3 hover:text-gold-300"
              >
                <Mail className="h-4 w-4" /> {site.email}
              </a>
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noreferrer"
                className="flex gap-3 hover:text-gold-300"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Inquiry
              </a>
              <p className="flex gap-3">
                <MapPin className="h-4 w-4 shrink-0" /> {site.address}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-white/50 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 Skand Tech Hub. All rights reserved.</p>
          <p>{site.tagline}</p>
        </div>
      </Container>
    </footer>
  );
}

function FooterColumn({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h2 className="font-semibold text-white">{title}</h2>
      <div className="mt-4 grid gap-3 text-sm text-white/60 [&_a:hover]:text-gold-300">
        {children}
      </div>
    </div>
  );
}
